// The entry bar: the only part of this page a technician actually operates.
//
// ── WHAT MAKES IT FAST ───────────────────────────────────────────────────────
// Three inputs identify a board, and TWO OF THEM DO NOT CHANGE while somebody
// works through a tray of the same part. So model and board type are locked
// chips, persisted to localStorage, and only the third field takes a keystroke.
// The field clears and refocuses itself after every add, so a tray of forty is
// forty scans and nothing else.
//
// ── ONE BEHAVIOR, NOT TWO ───────────────────────────────────────────────────
// A resolved board is added IMMEDIATELY, with whatever reason the station already
// recorded. It is not held in a confirm step: the row lands in the table where it
// is editable anyway, the reason pass fixes blanks in bulk, and the send-time
// guard refuses a manifest with an unreasoned line. Adding a confirm click per
// board would cost forty clicks to prevent a mistake the table already shows.
//
// A MISS stops the flow, because a miss needs a decision: this board has no
// record, so it becomes a DOA row or nothing. A RETIRED tracked unit stops it
// too, with no decision to offer: it is said in place and nothing is added.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ScanInput } from '../../components/BarcodeScanner';
import { Alert } from '../../components/ui/Alert';
import { buttonClass } from '../../components/ui/Button';
import { Field, fieldClass } from '../../components/ui/Field';
import { Panel } from '../../components/ui/Panel';
import { Select } from '../../components/ui/Select';
import { SegTrack } from '../../components/ui/SegTrack';
import { formatTimestamp } from '../../utils/date-utils';
import {
  BOARD_TYPES,
  MISS_COPY,
  MODEL_SERIALS,
  PART_INSTANCE_STATUS_LABELS,
  type BoardType,
  type LookupHit,
  type LookupMissResult,
  type LookupPartInstanceHit,
  type LookupResult,
  type ModelSerial,
  type ReturnReason,
} from './boardReturnsApi';

// One key per selection. A tech who reloads mid-tray keeps their setup, which is
// the whole point of the selections being sticky in the first place.
const LS_MODEL = 'boardReturns.modelSerial';
const LS_TYPE = 'boardReturns.boardType';

function readStored<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw && (allowed as readonly string[]).includes(raw) ? (raw as T) : fallback;
  } catch {
    // Private browsing, or storage disabled. The defaults still work.
    return fallback;
  }
}

interface Props {
  disabled: boolean;
  reasons: ReturnReason[];
  onLookup: (input: { uid?: string; last4?: string; model_serial: string; board_type: string }) =>
  Promise<LookupResult | null>;
  onAddBoard: (hit: LookupHit) => Promise<boolean>;
  onAddPartInstance: (hit: LookupPartInstanceHit) => Promise<boolean>;
  onAddDoa: (input: {
    count: number;
    board_type: string;
    model_serial: string;
    reason_id: number | null;
    reason_raw: string | null;
    designator: string | null;
  }) => Promise<boolean>;
}

/** The scan field is the one thing on this page that gets operated, so it is the one thing that looks it: the large well. */
// The kit's large well, widened for the camera key on the right.
const scanFieldClass = `${fieldClass({ size: 'lg' })} pr-11 font-mono tracking-wide`;

const labelClass = 'block text-[11px] uppercase tracking-wider text-on-surface-variant mb-1';

const EntryBar: React.FC<Props> = ({
  disabled,
  reasons,
  onLookup,
  onAddBoard,
  onAddPartInstance,
  onAddDoa,
}) => {
  const [modelSerial, setModelSerial] = useState<ModelSerial>(() =>
    readStored(LS_MODEL, MODEL_SERIALS, 'F021'));
  const [boardType, setBoardType] = useState<BoardType>(() =>
    readStored(LS_TYPE, BOARD_TYPES, 'AP'));

  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  // The miss carries the code it was about, because the FIELD is cleared either
  // way. Leaving the text in place meant the next wedge scan concatenated onto
  // it — a 40-character uid that resolves to nothing, so a REGISTERED board got
  // offered as a DOA row.
  const [miss, setMiss] = useState<(LookupMissResult & { scanned: string }) | null>(null);
  // Same reason as `miss` for carrying the code. One shape for both hit kinds:
  // a board a send retired and a unit a send retired read the same way here.
  const [retired, setRetired] = useState<{
    scanned: string;
    what: string;
    status: string;
    since: string | null;
  } | null>(null);
  const [countError, setCountError] = useState('');

  const [doaCount, setDoaCount] = useState('1');
  const [doaReasonId, setDoaReasonId] = useState('');
  const [doaCustom, setDoaCustom] = useState('');
  const [doaDesignator, setDoaDesignator] = useState('');

  const codeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try {
      window.localStorage.setItem(LS_MODEL, modelSerial);
      window.localStorage.setItem(LS_TYPE, boardType);
    } catch {
      // Nothing to do; the selections still hold for this session.
    }
  }, [modelSerial, boardType]);

  /** Put the caret back where the next scan lands. */
  const refocus = useCallback(() => {
    const input = codeRef.current?.querySelector('input');
    if (input) input.focus();
  }, []);

  /**
   * A scan or a typed last4, resolved and added in one step.
   *
   * The two routes are told apart by LENGTH, not by a mode switch: four
   * characters is a serial tail, anything longer is a code the server resolves.
   * A mode toggle here would be a control that exists only to tell the software
   * something it can see for itself.
   */
  const submit = useCallback(
    async (raw: string) => {
      const value = raw.trim().toUpperCase();
      if (!value || busy) return;

      setBusy(true);
      setMiss(null);
      setRetired(null);
      try {
        const input = value.length <= 4
          ? { last4: value, model_serial: modelSerial, board_type: boardType }
          : { uid: value, model_serial: modelSerial, board_type: boardType };

        const result = await onLookup(input);
        if (!result) return;

        // Compared against the literal so the union narrows: `!result.found`
        // leaves TypeScript holding both arms.
        if (result.found === false) {
          setMiss({ ...result, scanned: value });
          setCode('');
          return;
        }

        if (result.retired) {
          setRetired(result.kind === 'part_instance'
            ? {
              scanned: value,
              what: result.instance.display_name || result.instance.sku || 'This unit',
              status: PART_INSTANCE_STATUS_LABELS[result.instance.status] || result.instance.status,
              since: result.instance.retired_at,
            }
            : {
              scanned: value,
              what: `${result.board.board_type} board ${result.board.primary_uid}`,
              status: 'Retired',
              since: result.board.retired_at,
            });
          setCode('');
          return;
        }

        if (result.kind === 'part_instance') {
          await onAddPartInstance(result);
          setCode('');
          return;
        }

        await onAddBoard(result);
        // Cleared whatever the outcome. A board already on another manifest is
        // also "done with this code", and keeping it would concatenate the same
        // way a miss did.
        setCode('');
      } finally {
        setBusy(false);
        refocus();
      }
    },
    [busy, modelSerial, boardType, onLookup, onAddBoard, onAddPartInstance, refocus],
  );

  /** The custom reason wins when it has words, because somebody typed it. */
  const resolveDoaReason = () => {
    const custom = doaCustom.trim();
    if (custom) return { reason_id: null, reason_raw: custom };
    if (doaReasonId) {
      const picked = reasons.find((r) => String(r.id) === doaReasonId);
      return { reason_id: picked ? picked.id : null, reason_raw: picked ? picked.label : null };
    }
    return { reason_id: null, reason_raw: null };
  };

  const addDoa = async () => {
    if (busy) return;
    const count = Math.floor(Number(doaCount) || 0);
    if (count <= 0) {
      // Was a bare `return`, so clicking with an empty or non-numeric field did
      // nothing at all and read as a broken button.
      setCountError('How many boards? A whole number, 1 or more.');
      return;
    }
    setCountError('');
    setBusy(true);
    try {
      const { reason_id, reason_raw } = resolveDoaReason();
      const ok = await onAddDoa({
        count,
        board_type: boardType,
        model_serial: modelSerial,
        reason_id,
        reason_raw,
        designator: doaDesignator.trim() || null,
      });
      if (ok) {
        setDoaCount('1');
        setDoaDesignator('');
        setMiss(null);
      }
    } finally {
      setBusy(false);
    }
  };

  /** The miss path: this board has no record, so record it as one that never did. */
  const addMissAsDoa = async () => {
    setBusy(true);
    try {
      const { reason_id, reason_raw } = resolveDoaReason();
      const ok = await onAddDoa({
        count: 1,
        board_type: miss?.board_type || boardType,
        model_serial: modelSerial,
        reason_id,
        reason_raw,
        designator: doaDesignator.trim() || null,
      });
      if (ok) {
        setCode('');
        setMiss(null);
      }
    } finally {
      setBusy(false);
      refocus();
    }
  };

  return (
    <Panel padding="none" label="Add boards" className="overflow-hidden">
      {/* The locked pair. Rendered as segmented keys rather than selects because their
          value is what a tech reads back mid-tray to check they are still on the
          right part, and a collapsed select hides exactly that. */}
      <div className="px-4 py-3 bg-surface-container flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider text-on-surface-variant">Model</span>
          <SegTrack
            label="Drone model"
            size="sm"
            options={MODEL_SERIALS.map((m) => ({ key: m, label: <span className="font-mono">{m}</span>, aria: m }))}
            value={modelSerial}
            onChange={(key) => setModelSerial(key as ModelSerial)}
          />
        </div>

        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider text-on-surface-variant">Board</span>
          <SegTrack
            label="Board type"
            size="sm"
            wrap
            options={BOARD_TYPES.map((t) => ({ key: t, label: <span className="font-mono">{t}</span>, aria: t }))}
            value={boardType}
            onChange={(key) => setBoardType(key as BoardType)}
          />
        </div>

        {/* The composed target, in the same monospace the field and the labels
            use. This is what a tech reads back mid-tray to check they are still on
            the right part, so it is stated rather than left to be inferred from
            two highlighted chips. It also replaces a sentence that explained the
            UI ("stays set until you change it") instead of showing it. */}
        <span className="ml-auto flex items-baseline gap-2">
          <span className="text-[11px] uppercase tracking-wider text-on-surface-variant">Adding</span>
          <span className="font-mono text-sm text-primary">{modelSerial} {boardType}</span>
        </span>
      </div>

      {/* 3:2 rather than 1:1. The two routes are not equals: scanning is what
          happens forty times, and a DOA burst happens once. Equal columns ranked
          them equal. */}
      <div className="p-4 grid gap-5 lg:grid-cols-5">
        {/* Route one, and the reason this page exists. */}
        <div className="lg:col-span-3">
          <label className={labelClass} htmlFor="board-return-code">
            Scan a code, or type the last four of the serial
          </label>
          <div ref={codeRef}>
            <ScanInput
              id="board-return-code"
              value={code}
              onChange={(v) => {
                setCode(v);
                setMiss(null);
                setRetired(null);
              }}
              onSubmit={submit}
              autoSubmitOnScan
              formats={['qr', 'code128']}
              scanTitle="Scan a board"
              scanHint="Board QR, or a drone serial"
              placeholder={`${boardType} board QR, or 1D1C`}
              // NOT disabled while busy. Disabling a focused input blurs it, so a
              // tech scanning continuously lost either the whole next code or its
              // leading characters, with nothing on screen to say so. `submit`
              // already refuses re-entry on its own `busy` check.
              disabled={disabled}
              autoFocus
              inputClassName={scanFieldClass}
            />
          </div>
          <p className="mt-2 text-xs text-on-surface-variant">
            Four characters reads as a serial tail. Anything longer is looked up whole.
          </p>

          <Alert
              show={!!miss}
              tone="warning"
              className="mt-3"
              title={miss ? MISS_COPY[miss.miss] : undefined}
              action={(
                <button
                  type="button"
                  onClick={addMissAsDoa}
                  disabled={busy}
                  className={buttonClass('filled', 'sm')}
                >
                  Add as a DOA board
                </button>
              )}
            >
              <span className="block text-xs font-mono break-all">
                {miss?.drone ? miss.drone.serialnumber : miss?.scanned}
              </span>
            </Alert>

          <Alert
              show={!!retired}
              tone="warning"
              className="mt-3"
              title={retired ? `${retired.what} is ${retired.status.toLowerCase()}. Nothing was added.` : undefined}
            >
              <span className="block text-xs font-mono break-all">{retired?.scanned}</span>
              {retired?.since && (
                <span className="mt-1 block text-xs">
                  Since {formatTimestamp(retired.since)}, most likely on a Vendor RMA.
                </span>
              )}
            </Alert>
        </div>

        {/* Route two: it never had a record. A count, not a scan.
            A hairline in the DARK direction, not a light one: the two halves are
            the same elevation, so this separates rather than raising. */}
        <div className="lg:col-span-2 lg:border-l lg:border-black/25 lg:pl-5">
          <span className={labelClass}>Boards that never flashed</span>
          <div className="flex items-end gap-2">
            <div className="w-20">
              <Field
                aria-label="How many DOA boards"
                className="text-center tabular-nums"
                inputMode="numeric"
                value={doaCount}
                onChange={(e) => setDoaCount(e.target.value)}
                disabled={disabled || busy}
              />
            </div>
            <button
              type="button"
              onClick={addDoa}
              disabled={disabled || busy}
              className={buttonClass('quiet')}
            >
              Add DOA rows
            </button>
            <span className="text-xs text-on-surface-variant pb-2">
              as {modelSerial} {boardType}
            </span>
          </div>
          <Alert show={!!countError} tone="warning" variant="text" className="mt-1.5 !text-xs !leading-4">{countError}</Alert>

          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <div>
              <label className={labelClass} htmlFor="doa-reason">
                Reason
              </label>
              <Select
                id="doa-reason"
                label="Reason"
                value={doaReasonId}
                onChange={setDoaReasonId}
                placeholder="Pick one"
                disabled={disabled || busy || Boolean(doaCustom.trim())}
                options={[
                  { key: '', label: 'Pick one' },
                  ...reasons.map((r) => ({ key: String(r.id), label: r.label })),
                ]}
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="doa-designator">
                Designator <span className="normal-case tracking-normal">(optional)</span>
              </label>
              <Field
                id="doa-designator"
                value={doaDesignator}
                onChange={(e) => setDoaDesignator(e.target.value)}
                placeholder="U7"
                maxLength={40}
                disabled={disabled || busy}
              />
            </div>
          </div>

          <div className="mt-2">
            <label className={labelClass} htmlFor="doa-custom">
              Or describe it
            </label>
            <Field
              id="doa-custom"
              value={doaCustom}
              onChange={(e) => setDoaCustom(e.target.value)}
              placeholder="Connector shell split open"
              disabled={disabled || busy}
            />
            {/* Says what happens next, so a typed reason does not look like it
                went nowhere: it is countable only after the reason pass runs. */}
            <p className="mt-1 text-xs text-on-surface-variant">
              {doaCustom.trim()
                ? 'Used instead of the picked reason. Process reasons turns it into a code.'
                : 'Anything not in the list. Overrides the picker.'}
            </p>
          </div>
        </div>
      </div>
    </Panel>
  );
};

export default EntryBar;
